import { Component, ElementRef, OnInit } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { ToastService } from '@services/common/toast.service';
import { UserAction, UserStatus } from '@state/user.state';
import { AuthenticationService, CognitoAuthenticationService } from 'app/auth/service';
import { CustomValidators } from 'app/main/helpers/custom-validators';
import { revealFormErrors } from 'app/main/helpers/form-validation';

@Component({
    selector: 'app-register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.scss'],
    standalone: false,
})
export class RegisterComponent implements OnInit {
    formGroup: FormGroup = new FormGroup({});
    loading: boolean = false;

    //Terms and conditions handeling
    public termsAccepted: boolean = false;
    public submitAttempted: boolean = false;

    constructor(
        private _formBuilder: UntypedFormBuilder,
        private authService: AuthenticationService,
        private router: Router,
        private route: ActivatedRoute,
        private _toastService: ToastService,
        private translateService: CustomTranslateService,
        private readonly cognitoAuthenticationService: CognitoAuthenticationService,
        private store: Store,
        private readonly host: ElementRef<HTMLElement>,
    ) {}

    ngOnInit(): void {
        this.initializeForm();
    }

    onSubmit() {
        // stop here if the form is incomplete, showing what is missing
        this.submitAttempted = true;
        if (!revealFormErrors(this.formGroup, this.host.nativeElement) || !this.termsAccepted) {
            return;
        }
        const email: string = this.formGroup.controls['email'].value as string;
        const firstName: string = this.formGroup.controls['firstName'].value as string;
        const lastName: string = this.formGroup.controls['lastName'].value as string;
        const password: string = this.formGroup.controls['password'].value as string;
        const companyName: string = this.formGroup.controls['companyName'].value as string;
        const language: string = this.translateService.currentLang;
        this.loading = true;
        this.authService.signUp(email, password, firstName, lastName, language, companyName).subscribe({
            next: (username) => {
                this.loading = false;
                this.authService.beginValidation(username, password);
                this.router.navigate(['auth', 'verification'], { relativeTo: this.route.root });
                this.store.dispatch(new UserAction.UpdateUserStatus(UserStatus.TERMS));
            },
            error: (err: { code?: string }) => {
                if (err.code === 'UsernameExistsException') {
                    this._toastService.showError({
                        details: 'authentication.register.errors.userExists',
                        life: 5000,
                    });
                } else if (err.code === 'NetworkError') {
                    this._toastService.showError({
                        details: 'common.networkError',
                        life: 5000,
                    });
                } else {
                    this._toastService.showError({
                        details: 'common.error',
                        life: 5000,
                    });
                }
                this.loading = false;
            },
        });
    }

    private initializeForm() {
        this.formGroup = this._formBuilder.group(
            {
                firstName: ['', Validators.required],
                lastName: ['', Validators.required],
                companyName: ['', Validators.required],
                email: ['', [Validators.required, Validators.email]],
                password: ['', [Validators.required, CustomValidators.passwordValidator]],
                confirmPassword: ['', Validators.required],
            },
            {
                validators: [CustomValidators.mustMatch('password', 'confirmPassword')],
            },
        );

        this.formGroup.get('email')?.valueChanges.subscribe((value: string) => {
            if (value) {
                const lowerCaseValue = value.toLowerCase();
                if (value !== lowerCaseValue) {
                    this.formGroup.get('email')?.setValue(lowerCaseValue, { emitEvent: false });
                }
            }
        });
    }
}
